import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { IMoodRecord, IMoodRecordDraft } from '@shared/types';
import { useLocation } from '@/features/routing';
import { NotLoggedInError } from '../../components/ErrorBoundary/types';
import { SECTIONS } from '../../features/routing/constants';
import { parsePathname } from '../../features/routing/utils';
import { createMoodRecordDraft } from './createMoodRecordDraft';
import * as storage from './moodRecordDraftStorage';

export const MoodRecordDraftContext = createContext<{
  initializeDraft: (moodRecord?: IMoodRecord) => void;
  moodRecordDraft: IMoodRecordDraft | null;
  photoFile?: File;
  removeDraft: () => Promise<void>;
  setPhotoFile: (photoFile?: File) => void;
  updateMoodRecordDraft: (data: Partial<IMoodRecordDraft>) => Promise<boolean>;
}>({
  initializeDraft: () => {},
  moodRecordDraft: null,
  photoFile: undefined,
  removeDraft: () => Promise.resolve(),
  setPhotoFile: () => {},
  updateMoodRecordDraft: () => Promise.resolve(false),
});

interface IMoodRecordDraftProviderProps {
  ownerUid: string;
  children: React.ReactNode;
}

/**
 * When editing an existing mood record, call initializeDraft with the IMoodRecord.
 * Do this before navigating to the mood log entry pages.
 */
export const MoodRecordDraftProvider = ({ ownerUid, children }: IMoodRecordDraftProviderProps) => {
  const [moodRecordDraft, setMoodRecordDraft] = useState<IMoodRecordDraft | null>(null);
  const [photoFile, setPhotoFile] = useState<File | undefined>(undefined);

  const initializeDraft = useCallback(
    async (moodRecord?: IMoodRecord): Promise<boolean> => {
      if (typeof ownerUid !== 'string' || ownerUid.trim() === '') {
        console.log(
          '\x1b[31m%s\x1b[0m',
          'in initializeDraft() -> this is the erroneously constructed ownerUid:',
          ownerUid
        );
        throw new NotLoggedInError();
      }
      if (moodRecord) {
        console.log(
          `%cinitializing draft from existing mood record...`,
          'background:#bada55; color:#222;'
        );
        const draft = createMoodRecordDraft(ownerUid, moodRecord);
        setMoodRecordDraft(draft);
        return storage.saveDraft(draft);
      } else {
        const currentDraft = storage.getCurrentDraft();
        if (currentDraft) {
          // console.log(
          //   `%cinitializing draft from current draft...`,
          //   'background:#bada55; color:#222;'
          // );
          setMoodRecordDraft(currentDraft);
          return true;
        } else {
          const draft = createMoodRecordDraft(ownerUid);
          console.log(`%cinitializing a brand new draft...`, 'background:#bada55; color:#222;');
          setMoodRecordDraft(draft);
          return storage.saveDraft(draft);
        }
      }
    },
    [ownerUid]
  );

  // For debugging only.
  useEffect(() => {
    if (moodRecordDraft) {
      // @ts-expect-error - this is for debugging purposes.
      window.tt = moodRecordDraft;
    }
  }, [moodRecordDraft]);

  // Update the draft in the context and storage.
  const updateMoodRecordDraft = useCallback(
    async (data: Partial<IMoodRecordDraft>): Promise<boolean> => {
      if (!moodRecordDraft) return false;
      const updatedDraft = { ...moodRecordDraft, ...data } as IMoodRecordDraft;
      setMoodRecordDraft(updatedDraft);
      const success = await storage.saveDraft(updatedDraft);
      return success;
    },
    [moodRecordDraft]
  );

  // Remove the draft and photo from the context and storage.
  const removeDraft = useCallback(async () => {
    const { moodDate } = moodRecordDraft || {};
    if (!moodDate) return;

    // @ts-expect-error - this is for debugging purposes.
    window.tt = undefined;
    setMoodRecordDraft(null);
    setPhotoFile(undefined);

    await storage.deleteDraft(ownerUid, moodDate).catch((error) => {
      console.error('Error deleting draft:', error);
    });
  }, [ownerUid, moodRecordDraft]);

  const pathname = useLocation();
  useEffect(() => {
    const { section, stepNumber } = parsePathname(pathname);
    const isMoodLogSection = section === SECTIONS.Mood_log_entry;

    // If the user is logging mood, initialize the draft if it doesn't exist.
    if (isMoodLogSection && !moodRecordDraft && stepNumber !== null) {
      // console.log(
      //   `%ccalling initializeDraft here in useEffect in MoodRecordDraftContext.tsx`,
      //   'color: #221; background: #F40;padding: 2px 8px; font-weight: bold'
      // );
      initializeDraft();
    }

    // If the user navigates away from the mood log entry section, remove the draft and photo.
    if (!isMoodLogSection && moodRecordDraft) {
      removeDraft();
    }
  }, [pathname, ownerUid, moodRecordDraft, initializeDraft, removeDraft]);

  return (
    <MoodRecordDraftContext.Provider
      value={{
        initializeDraft,
        moodRecordDraft,
        photoFile,
        removeDraft,
        setPhotoFile,
        updateMoodRecordDraft,
      }}
    >
      {children}
    </MoodRecordDraftContext.Provider>
  );
};

export const useMoodRecordDraft = () => {
  const context = useContext(MoodRecordDraftContext);
  if (!context) {
    throw new Error('useMoodRecordDraft must be used within a MoodRecordDraftProvider');
  }
  return context;
};
